import type {
  HomeFeedItemType,
  HomeFeedLogItem,
  ProactiveKind,
  ProactiveLogItem,
  ProactiveOutcome,
} from "@/types";

/**
 * Presentation rules for the engagement console.
 * Spec: base_agent_spec/engagement_admin_console.md
 *
 * The wording here matters more than usual: the audience is non-engineers
 * deciding what to change, and two of these labels are actively misleading if
 * phrased loosely (see `outcomeLabel`).
 */

type Tone = "neutral" | "good" | "warn" | "bad";

export type OutcomeDisplay = {
  label: string;
  tone: Tone;
  /** Shown on hover — the label alone can't carry the caveat. */
  hint: string;
};

const OUTCOME: Record<ProactiveOutcome, OutcomeDisplay> = {
  // Not a user reaction at all. Kept visually distinct from `no_signal` so
  // nobody reads an infrastructure failure as disinterest.
  not_delivered: {
    label: "전달 안 됨",
    tone: "bad",
    hint: "생성은 됐지만 사용자에게 닿지 않았습니다. 대화 기록에서도 숨겨지고 푸시도 나가지 않았습니다. 콘텐츠 품질 판단에 쓰면 안 됩니다.",
  },
  replied: { label: "답장", tone: "good", hint: "같은 세션에서 답장했습니다." },
  returned: {
    label: "복귀",
    tone: "good",
    hint: "답장은 없었지만 48시간 안에 다른 세션에서 활동했습니다.",
  },
  dismissed: { label: "알림 닫음", tone: "warn", hint: "푸시 알림을 치웠습니다." },
  opened: { label: "열람", tone: "neutral", hint: "봤지만 반응은 없었습니다." },
  delivered: {
    label: "전달됨",
    tone: "neutral",
    hint: "푸시는 전달됐지만 열람 여부는 확인되지 않았습니다.",
  },
  // The single most misread value on this screen.
  no_signal: {
    label: "미관측",
    tone: "neutral",
    hint: "반응이 없었다는 뜻이 아니라, 관측되지 않았다는 뜻입니다. 푸시를 끈 사용자, 알림 확인이 실패한 기기, 앱 이벤트 보강 이전 발송이 모두 여기에 들어갑니다.",
  },
};

export function outcomeDisplay(outcome: ProactiveOutcome): OutcomeDisplay {
  return OUTCOME[outcome] ?? OUTCOME.no_signal;
}

/** Outcomes that must not be counted as "the user didn't react". */
export function isUnobserved(outcome: ProactiveOutcome) {
  return outcome === "no_signal" || outcome === "not_delivered";
}

const KIND_LABEL: Record<ProactiveKind, string> = {
  auto: "자동",
  manual_direct: "수동(직접)",
  manual_compose: "수동(AI작성)",
};

export function kindLabel(kind: ProactiveKind) {
  return KIND_LABEL[kind] ?? kind;
}

const FEED_TYPE_LABEL: Record<HomeFeedItemType, string> = {
  suggestion: "추천질문",
  reply_reminder: "답장리마인더",
};

export function feedTypeLabel(type: HomeFeedItemType) {
  return FEED_TYPE_LABEL[type] ?? type;
}

/** `"12분 후"` / `"3시간 후"` — relative duration, not a clock time. */
export function relativeDuration(seconds: number | null | undefined) {
  if (seconds === null || seconds === undefined) return "";
  if (seconds < 60) return `${Math.max(seconds, 0)}초 후`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}분 후`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}시간 후`;
  return `${Math.round(seconds / 86400)}일 후`;
}

function durationBetween(from: string | null, to: string | null | undefined) {
  if (!from || !to) return null;
  const start = new Date(from).getTime();
  const end = new Date(to).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return Math.round((end - start) / 1000);
}

/**
 * The extra detail shown next to the outcome badge.
 *
 * Deliberately empty for `no_signal` / `not_delivered` — there is nothing
 * true to say, and inventing a phrase there is how "미관측" starts reading
 * as "무시함".
 */
export function outcomeDetail(item: ProactiveLogItem): string {
  switch (item.outcome) {
    case "replied":
      return relativeDuration(item.reply_latency_sec);
    case "returned":
      return relativeDuration(
        durationBetween(item.created_at, item.signals.next_activity_at),
      );
    case "opened":
      return relativeDuration(
        durationBetween(item.created_at, item.signals.opened_at?.value),
      );
    default:
      return "";
  }
}

/** True when a value was inferred rather than observed → badge it 추정. */
export function isDerived(source: string | null | undefined) {
  return source === "derived";
}

function line(label: string, value: unknown) {
  return value === null || value === undefined || value === ""
    ? null
    : `${label}: ${value}`;
}

/**
 * Plain-text summary of one send, for the clipboard.
 *
 * The generation prompts live in server code and cannot be edited from this
 * screen or Prompt Lab, so handing a concrete case to the dev team *is* the
 * improvement path here. This exists to remove the friction from that step.
 *
 * Deliberately excludes the surrounding conversation: those are other
 * people's messages, and the caller-private boundary applies to an export
 * just as much as to the screen.
 */
export function proactiveCaseText(item: ProactiveLogItem): string {
  const display = outcomeDisplay(item.outcome);
  const opened = item.signals.opened_at;
  return [
    "[선제 메시지 사례]",
    line("환경", item._environment),
    line("발송", item.created_at),
    line("유형", kindLabel(item.kind)),
    line("수신자", item.recipient_email),
    line(
      "세션",
      `${item.session.display_name || "(이름 없음)"} (${item.session.session_id})`,
    ),
    line("요청자", item.requested_by),
    line("메시지 ID", item.message_id),
    "",
    "본문:",
    item.content || "(없음)",
    "",
    line("반응", `${display.label}${outcomeDetail(item) ? ` · ${outcomeDetail(item)}` : ""}`),
    line("전달", item.signals.delivered_at),
    line(
      "열람",
      opened ? `${opened.value}${isDerived(opened.source) ? " (추정)" : ""}` : null,
    ),
    line("답장", item.signals.replied_at),
    line("다른 세션 복귀", item.signals.next_activity_at),
    line("종료 사유", item.finish_reason),
  ]
    .filter((v) => v !== null)
    .join("\n");
}

export function homeFeedCaseText(item: HomeFeedLogItem): string {
  const conversion = item.conversion;
  return [
    "[홈 피드 사례]",
    line("환경", item._environment),
    line("생성", item.generated_at),
    line("타입", feedTypeLabel(item.type)),
    line("상태", item.state),
    line("사용자", item.email),
    line(
      "세션",
      `${item.session.display_name || "(이름 없음)"} (${item.session.session_id})`,
    ),
    line("항목 ID", item.home_feed_item_id),
    "",
    "내용:",
    item.text || item.title || "(없음)",
    "",
    line("노출", item.events.impression_at),
    line("열람", item.events.opened_at),
    line("무시", item.events.dismissed_at),
    line("전송", item.events.sent_at),
    conversion
      ? line(
          "전환 메시지",
          `${conversion.text || conversion.message_id}${
            isDerived(conversion.source) ? " (추정)" : ""
          } · 이후 ${conversion.turn_count}턴`,
        )
      : null,
  ]
    .filter((v) => v !== null)
    .join("\n");
}
